<template>
  <div class="course-play">
    <header class="course-play__bar">
      <button type="button" class="course-play__back" @click="goBack">← 返回课程</button>
      <div class="course-play__title">{{ title || '全屏播放' }}</div>
      <button
        v-if="src && !isM3u8"
        type="button"
        class="course-play__landscape"
        @click="toggleLandscape"
      >
        {{ landscapeFs ? '退出横屏' : '横屏' }}
      </button>
      <span v-else class="course-play__landscape-spacer" />
    </header>

    <main class="course-play__main" :class="{ 'course-play__main--landscape': landscapeFs }">
      <div v-if="loading" class="course-play__state">加载中…</div>
      <div v-else-if="error" class="course-play__state">
        <p>{{ error }}</p>
        <button type="button" class="course-play__back" @click="goBack">返回课程</button>
      </div>
      <div v-else class="course-play__stage">
        <video-player v-if="isM3u8" :options="m3u8Options" />
        <video
          v-else
          ref="videoEl"
          :src="src"
          class="course-play__video"
          controls
          playsinline
          webkit-playsinline
          preload="auto"
          x5-playsinline="true"
          x5-video-player-type="h5"
          x5-video-player-fullscreen="true"
          x5-video-orientation="landscape"
          autoplay
        />
      </div>
    </main>
  </div>
</template>

<script>
import VideoPlayer from '@/components/VideoPlayer.vue'
import {
  applyWechatVideoAttrs,
  exitNativeFullscreen,
  readCoursePlayPayload,
  tryLockLandscape,
  tryNativeVideoFullscreen,
  tryUnlockOrientation
} from '@/utils/wechatVideo'

export default {
  name: 'CourseVideoPlayView',
  components: { VideoPlayer },
  data() {
    return {
      loading: true,
      error: '',
      src: '',
      title: '',
      courseId: 0,
      kpId: 0,
      isM3u8: false,
      landscapeFs: false
    }
  },
  computed: {
    m3u8Options() {
      return {
        fluid: true,
        autoplay: true,
        controls: true,
        preload: 'auto',
        playsinline: true,
        sources: [{ src: this.src, type: 'application/x-mpegURL' }]
      }
    }
  },
  mounted() {
    this.bootstrap()
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown)
    this.leaveLandscape(true)
  },
  methods: {
    onKeydown(e) {
      if (e.key === 'Escape' && this.landscapeFs) this.leaveLandscape()
    },
    bootstrap() {
      const courseId = parseInt(this.$route.params.courseId, 10) || 0
      const fromStore = readCoursePlayPayload()
      const src = String(this.$route.query.src || fromStore.src || '').trim()
      const title = String(this.$route.query.title || fromStore.title || '').trim()
      const kpId = parseInt(this.$route.query.kpId || fromStore.kpId, 10) || 0
      this.courseId = courseId || Number(fromStore.courseId) || 0
      this.kpId = kpId
      this.title = title
      this.src = src
      this.isM3u8 = !!src && src.endsWith('.m3u8')
      this.loading = false
      if (!this.src) {
        this.error = '没有可播放的视频，请返回课程后重试'
        return
      }
      this.$nextTick(() => {
        applyWechatVideoAttrs(this.$refs.videoEl)
      })
    },
    goBack() {
      const id = this.courseId || this.$route.params.courseId
      if (id) {
        this.$router.replace(`/knowledge/courses/display/${id}`)
      } else {
        this.$router.back()
      }
    },
    async toggleLandscape() {
      if (this.landscapeFs) {
        await this.leaveLandscape()
        return
      }
      const video = this.$refs.videoEl
      applyWechatVideoAttrs(video)
      await tryNativeVideoFullscreen(video)
      await tryLockLandscape()
      this.landscapeFs = true
      document.body.classList.add('course-video-landscape-lock')
      try {
        await video?.play()
      } catch (_) {
        /* ignore */
      }
    },
    async leaveLandscape(silent) {
      this.landscapeFs = false
      document.body.classList.remove('course-video-landscape-lock')
      await exitNativeFullscreen()
      await tryUnlockOrientation()
      if (!silent) {
        /* no-op */
      }
    }
  }
}
</script>

<style scoped>
.course-play {
  position: fixed;
  inset: 0;
  z-index: 10040;
  display: flex;
  flex-direction: column;
  background: #000;
  color: #fff;
}

.course-play__bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px calc(10px + env(safe-area-inset-top, 0px));
  padding-top: calc(10px + env(safe-area-inset-top, 0px));
  background: rgba(0, 0, 0, 0.88);
}

.course-play__back,
.course-play__landscape {
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  cursor: pointer;
}

.course-play__landscape-spacer {
  width: 52px;
  flex-shrink: 0;
}

.course-play__title {
  flex: 1;
  min-width: 0;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.course-play__main {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0 env(safe-area-inset-bottom, 0px);
}

.course-play__stage {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
}

.course-play__video {
  width: 100%;
  height: 100%;
  max-height: 100%;
  object-fit: contain;
  background: #000;
}

.course-play__stage :deep(.video-js) {
  width: 100% !important;
  height: 100% !important;
  min-height: 50vh;
}

.course-play__state {
  text-align: center;
  padding: 24px;
  color: #ddd;
  line-height: 1.6;
}

.course-play__main--landscape .course-play__stage {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100vh;
  height: 100vw;
  transform: translate(-50%, -50%) rotate(90deg);
}

.course-play__main--landscape .course-play__video {
  width: 100%;
  height: 100%;
}
</style>
